// Builders for merchant-facing app links produced by hades (AS-11463).
//
// my.assisty.ai is routed by Kong to warbler or atlas per shop, read from the
// `assisty_shop` URL param (then `shop`). Never put `shop=` on a top-level URL:
// the Shopify App Bridge script redirects any top-level document carrying it
// into the Shopify admin.

export const APP_BASE_URL = 'https://my.assisty.ai';
export const SHOPIFY_ADMIN_BASE_URL = 'https://admin.shopify.com/store';

const MYSHOPIFY_SUFFIX = '.myshopify.com';
// One DNS label: 1-63 chars, no leading or trailing hyphen.
const SHOP_HANDLE_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Normalises `name` or `name.myshopify.com` (any case) to the store handle.
 * Returns null when the input is missing or not a valid handle.
 */
export function toShopHandle(shop: string | null | undefined): string | null {
  if (!shop) {
    return null;
  }
  let handle = String(shop).trim().toLowerCase();
  if (handle.endsWith(MYSHOPIFY_SUFFIX)) {
    handle = handle.slice(0, -MYSHOPIFY_SUFFIX.length);
  }
  return SHOP_HANDLE_PATTERN.test(handle) ? handle : null;
}

/** Full `<handle>.myshopify.com` domain, or null when the shop is invalid. */
export function toShopDomain(shop: string | null | undefined): string | null {
  const handle = toShopHandle(shop);
  return handle ? `${handle}${MYSHOPIFY_SUFFIX}` : null;
}

/**
 * Staff "login as merchant" link. `assisty_shop` lets Kong route straight to
 * the store's shell (warbler or atlas); it is omitted when the shop is unknown.
 */
export function buildImpersonationUrl(token: string, shopName?: string | null): string {
  const url = `${APP_BASE_URL}?hades=${token}`;
  const domain = toShopDomain(shopName);
  return domain ? `${url}&assisty_shop=${encodeURIComponent(domain)}` : url;
}

/**
 * Per-store discount link that opens the app inside the Shopify admin.
 * Returns null when the store or the discount code is missing/invalid.
 */
export function buildDiscountAdminUrl(
  shop: string | null | undefined,
  discountCode: string | null | undefined,
): string | null {
  const handle = toShopHandle(shop);
  if (!handle || !discountCode) {
    return null;
  }
  return `${SHOPIFY_ADMIN_BASE_URL}/${encodeURIComponent(handle)}/apps/assisty/auto_subscription`
    + `?discount=${encodeURIComponent(discountCode)}`;
}
